import React from 'react';
import { Autocomplete, Box, Checkbox, Chip, IconButton, TextField, Tooltip, Typography, alpha } from '@mui/material';
import type { SortingState } from '@tanstack/react-table';
import ColumnHeaderFilterIcon from '../common/ColumnHeaderFilterIcon';
import { SEARCE_COLORS } from '../../theme';
import type { BillingBudget, BillingBudgetField } from '../../types/billing';
import { BILLING_NUMERIC_FIELDS, BILLING_TABLE_COLUMNS } from '../../types/billing';
import { formatBillingNumber } from '../../utils/billingHelpers';
import {
  BILLING_FILTERABLE_COLUMNS,
  BILLING_SORTABLE_COLUMNS,
  BILLING_STICKY_COLUMN_IDS,
  getBillingColumnLabel,
  getMonthPrefixFromField,
  MONTH_DISPLAY_LABELS,
} from '../../utils/billingTableConfig';
import type { DataTableColumnDef } from '../table/dataTableTypes';

const MONTH_SUB_COL_WIDTH = 128;

/** Under budget / nearing budget / over budget thresholds for the combined spend/budget cell. */
const NEAR_BUDGET_THRESHOLD = 0.8;

function monthUtilizationColor(spend: number | null, budget: number | null): string | null {
  if (budget === null || budget === undefined || budget <= 0) return null;
  if (spend === null || spend === undefined) return null;
  const ratio = spend / budget;
  if (ratio > 1) return SEARCE_COLORS.error;
  if (ratio >= NEAR_BUDGET_THRESHOLD) return SEARCE_COLORS.warning;
  return SEARCE_COLORS.success;
}

type ActiveCell = { accountId: string; field: BillingBudgetField } | null;

function statusChipColor(status: string | null): 'success' | 'warning' | 'error' | 'default' {
  const s = (status ?? '').toLowerCase();
  if (s.includes('ok') || s.includes('good') || s.includes('on track')) return 'success';
  if (s.includes('warn') || s.includes('risk')) return 'warning';
  if (s.includes('over') || s.includes('exceed') || s.includes('critical')) return 'error';
  return 'default';
}

function BillingSortFilterHeader(props: {
  label: string;
  columnId: BillingBudgetField;
  sorting: SortingState;
  isFilterActive: boolean;
  onFilterClick: (columnId: BillingBudgetField, e: React.MouseEvent<HTMLElement>) => void;
  center?: boolean;
}) {
  const { label, columnId, sorting, isFilterActive, onFilterClick, center } = props;
  const filterable = BILLING_FILTERABLE_COLUMNS.includes(columnId);
  const sortable = BILLING_SORTABLE_COLUMNS.includes(columnId);
  const sorted = sorting[0]?.id === columnId;

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: center ? 'center' : 'flex-start',
        gap: 0.25,
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          lineHeight: 1.25,
          color: SEARCE_COLORS.white,
          ...(center && { textAlign: 'center', flex: 1 }),
        }}
      >
        {label}
        {sortable && sorted && (sorting[0]?.desc ? ' ↓' : ' ↑')}
      </Typography>
      {filterable && (
        <Tooltip title={`Filter ${label}`}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onFilterClick(columnId, e);
            }}
            sx={{
              p: 0.25,
              color: SEARCE_COLORS.white,
              '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.2) },
            }}
          >
            <ColumnHeaderFilterIcon isFilterActive={isFilterActive} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function stickyShell(left: number, width: number, isHeader: boolean) {
  return {
    position: 'sticky' as const,
    left,
    zIndex: isHeader ? 12 : 4,
    minWidth: width,
    width,
    maxWidth: width,
    backgroundColor: isHeader ? SEARCE_COLORS.primary : SEARCE_COLORS.white,
    boxShadow: isHeader
      ? `inset -2px 0 0 ${alpha(SEARCE_COLORS.white, 0.35)}`
      : `inset -2px 0 0 ${SEARCE_COLORS.neutral_200}`,
  };
}

function stickyLeftForField(field: BillingBudgetField): number {
  let left = 0;
  for (const col of BILLING_TABLE_COLUMNS) {
    if (col.id === field) break;
    if (BILLING_STICKY_COLUMN_IDS.includes(col.id)) {
      left += col.minWidth ?? 100;
    }
  }
  return left;
}

export interface BuildSandboxBillingDataTableColumnsParams {
  sorting: SortingState;
  isFilterActive: (columnId: BillingBudgetField) => boolean;
  onFilterClick: (columnId: BillingBudgetField, e: React.MouseEvent<HTMLElement>) => void;
  onHeaderSortClick: (columnId: BillingBudgetField) => void;
  canEdit: boolean;
  editableFields: Set<BillingBudgetField>;
  activeCell: ActiveCell;
  draftValue: string;
  onDraftChange: (value: string) => void;
  onCommitActiveCell: () => void;
  onCancelActiveCell: () => void;
  onCellDoubleClick: (row: BillingBudget, field: BillingBudgetField) => void;
  getDisplayRow: (row: BillingBudget) => BillingBudget;
  isCellDirty: (accountId: string, field: BillingBudgetField) => boolean;
  /** Business Unit / Sub-BU are constrained to this org-wide list instead of free text. */
  businessUnitNames: string[];
  getSubBuNames: (businessUnit: string | null | undefined) => string[];
  onDropdownCommit: (row: BillingBudget, field: BillingBudgetField, value: string) => void;
  /** Row-structural edit mode: shows a selection checkbox in the Account ID cell. */
  editMode?: boolean;
  isRowSelected?: (accountId: string) => boolean;
  onToggleRowSelect?: (accountId: string) => void;
}

export function getSandboxBillingSortableColumnIds(): string[] {
  return [...BILLING_SORTABLE_COLUMNS];
}

export function buildSandboxBillingDataTableColumns(
  p: BuildSandboxBillingDataTableColumnsParams,
): DataTableColumnDef<BillingBudget>[] {
  const {
    sorting,
    isFilterActive,
    onFilterClick,
    onHeaderSortClick,
    canEdit,
    editableFields,
    activeCell,
    draftValue,
    onDraftChange,
    onCommitActiveCell,
    onCancelActiveCell,
    onCellDoubleClick,
    getDisplayRow,
    isCellDirty,
    editMode = false,
    isRowSelected,
    onToggleRowSelect,
    businessUnitNames,
    getSubBuNames,
    onDropdownCommit,
  } = p;

  return BILLING_TABLE_COLUMNS.filter((col) => {
    const prefix = getMonthPrefixFromField(col.id);
    // Spend is merged into the paired budget column's cell (shown as "spend / budget"
    // with under/near/over-budget coloring), so it no longer renders as its own column.
    return !(prefix && col.id.endsWith('_spend'));
  }).map((col) => {
    const field = col.id;
    const isSticky = BILLING_STICKY_COLUMN_IDS.includes(field);
    const monthPrefix = getMonthPrefixFromField(field);
    const spendField = monthPrefix ? (`${monthPrefix}_spend` as BillingBudgetField) : null;
    const width = monthPrefix ? MONTH_SUB_COL_WIDTH : (col.minWidth ?? 100);
    const label = getBillingColumnLabel(field);

    const headerLabel = monthPrefix ? MONTH_DISPLAY_LABELS[monthPrefix] : label;

    return {
      id: field,
      accessorKey: field,
      header: ({ column }) => (
        <BillingSortFilterHeader
          label={headerLabel}
          columnId={column.id as BillingBudgetField}
          sorting={sorting}
          isFilterActive={isFilterActive(column.id as BillingBudgetField)}
          onFilterClick={onFilterClick}
          center={Boolean(monthPrefix)}
        />
      ),
      meta: {
        columnMenuLabel: monthPrefix
          ? `${label.replace(/ (Spend|Budget)$/, '')} (Spend / Budget)`
          : label,
        minWidth: width,
        width,
        maxWidth: monthPrefix ? width : undefined,
        align: monthPrefix ? 'center' : 'left',
        headerCellProps: BILLING_SORTABLE_COLUMNS.includes(field)
          ? {
              onClick: () => onHeaderSortClick(field),
              sx: { cursor: 'pointer' },
            }
          : undefined,
        ...(isSticky
          ? {
              headerCellSx: stickyShell(stickyLeftForField(field), width, true),
              cellSx: stickyShell(stickyLeftForField(field), width, false),
            }
          : {}),
        noEllipsisTooltip: field === 'is_flagged' || field === 'status',
      },
      cell: ({ row }) => {
        const display = getDisplayRow(row);
        const isActive =
          activeCell?.accountId === row.account_id && activeCell.field === field;
        const editable = canEdit && editMode && field !== 'account_id' && editableFields.has(field);

        if (isActive) {
          if (field === 'business_unit' || field === 'sub_bu') {
            const options =
              field === 'business_unit' ? businessUnitNames : getSubBuNames(display.business_unit);
            return (
              <Autocomplete
                size="small"
                options={options}
                value={draftValue || null}
                autoFocus
                openOnFocus
                onChange={(_e, newValue) => onDropdownCommit(row, field, newValue ?? '')}
                onBlur={onCancelActiveCell}
                renderInput={(params) => <TextField {...params} autoFocus variant="outlined" />}
                fullWidth
                sx={{ minWidth: width - 16 }}
                componentsProps={{
                  popper: {
                    style: { width: 'max-content', minWidth: 260 },
                    placement: 'bottom-start',
                  },
                }}
              />
            );
          }
          return (
            <TextField
              size="small"
              variant="outlined"
              value={draftValue}
              autoFocus
              onChange={(e) => onDraftChange(e.target.value)}
              onBlur={onCommitActiveCell}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onCommitActiveCell();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelActiveCell();
                }
              }}
              fullWidth
              sx={{ minWidth: width - 16 }}
            />
          );
        }

        const value = display[field];
        let content: React.ReactNode;
        let utilizationColor: string | null = null;
        if (field === 'is_flagged') {
          content = (
            <Chip
              size="small"
              label={value ? 'Yes' : 'No'}
              color={value ? 'warning' : 'default'}
              variant={value ? 'filled' : 'outlined'}
            />
          );
        } else if (field === 'status' && value) {
          content = (
            <Chip size="small" label={String(value)} color={statusChipColor(display.status)} />
          );
        } else if (spendField) {
          const budgetVal = value as number | null;
          const spendVal = display[spendField] as number | null;
          utilizationColor = monthUtilizationColor(spendVal, budgetVal);
          content = `${formatBillingNumber(spendVal)} / ${formatBillingNumber(budgetVal)}`;
        } else if (BILLING_NUMERIC_FIELDS.includes(field)) {
          content = formatBillingNumber(value as number | null);
        } else {
          content = value != null && value !== '' ? String(value) : '—';
        }

        const dirty = isCellDirty(row.account_id, field);

        if (field === 'account_id' && editMode) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <Checkbox
                size="small"
                checked={isRowSelected?.(row.account_id) ?? false}
                onChange={() => onToggleRowSelect?.(row.account_id)}
                sx={{ p: 0.25 }}
              />
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {content}
              </Box>
            </Box>
          );
        }

        return (
          <Box
            onClick={() => editable && onCellDoubleClick(row, field)}
            sx={{
              width: '100%',
              ...(editable && !dirty && !utilizationColor && { backgroundColor: alpha(SEARCE_COLORS.success, 0.05) }),
              ...(utilizationColor && !dirty && { backgroundColor: alpha(utilizationColor, 0.14) }),
              ...(dirty && { backgroundColor: alpha(SEARCE_COLORS.primary, 0.08) }),
              ...(editable && { cursor: 'cell' }),
            }}
          >
            {content}
          </Box>
        );
      },
    };
  });
}
